import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Calendar, Clock, MapPin, Repeat, Users, Video } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import { detectMeetingProvider, meetingProviderBrand } from "../../lib/meetingProvider";
import { DateTimePicker } from "../../primitives/date-time-picker";
import { Switch } from "../../primitives/switch";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
  LoadPeopleOptions,
} from "../ActionFieldEditor";
import { RecipientsInput } from "../RecipientsInput";
import { RecurrenceField } from "./RecurrenceField";
import { parseRRule } from "./recurrence";
import { VendorPreviewField, VendorPreviewShell } from "./VendorPreviewShell";
import { asString, asStringArray, specOfName, useEnvelopeCommit } from "./envelope-primitives";

/**
 * Fields this preview OWNS as inline editors — the stacked ``ActionFieldEditor``
 * skips them (via ``previewOwnedFields``) so the operator never sees the same
 * input twice (the "form-on-top / mock-below" duplication). Names MUST match the
 * BE ``_synth_create_event_fields`` schema (``start_time`` / ``end_time`` — NOT
 * ``start`` / ``end``) or the stacked field survives and the value is dropped.
 * ``location`` + ``add_conference`` are honoured by the executor even though the
 * synth omits ``location`` from the stacked surface. The ``calendar_connection_id``
 * account picker is intentionally NOT owned → stays stacked, first (account-first).
 */
export const CALENDAR_PREVIEW_OWNED_FIELDS = [
  "title",
  "start_time",
  "end_time",
  "recurrence",
  "location",
  "attendees",
  "add_conference",
  "description",
] as const;
type CalendarFieldName = (typeof CALENDAR_PREVIEW_OWNED_FIELDS)[number];

export interface CalendarCreateEventPreviewProps {
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** When provided (post-execution), draws the executed copy read-only. */
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled API — present while the proposal is still editable. */
  readonly schema?: readonly ActionFieldSpec[];
  readonly values?: Readonly<Record<string, ActionFieldValue>>;
  readonly errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  readonly onChange?: (next: Record<string, ActionFieldValue>) => void;
  readonly disabled?: boolean;
  readonly className?: string;
  /**
   * People-search transport for the ``attendees`` field — same contract the
   * email ``to/cc/bcc`` recipients use, so guests autocomplete from the chosen
   * calendar account's People API (Google People + MS Graph). Omit → the
   * attendees input degrades to free-text chips.
   */
  readonly loadPeopleOptions?: LoadPeopleOptions;
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

/** Human duration between two instants (e.g. "1 h 30 min"). Empty when unknown. */
function formatDuration(startRaw: string, endRaw: string): string {
  if (!startRaw || !endRaw) return "";
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const mins = Math.round((end.getTime() - start.getTime()) / 60_000);
  if (mins <= 0) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** wire string → ``Date | null`` (accepts ``YYYY-MM-DDTHH:mm`` local + ISO). */
function parseDate(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ``Date`` → ``YYYY-MM-DDTHH:mm`` — the local wire format the action schema
 * uses for ``datetime`` fields (matches ``ActionFieldEditor``'s DateRenderer). */
function formatWire(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// SOTA calendar composer defaults (Google Calendar / Notion Calendar / Cron,
// 2026): open on the NEXT quarter/half-hour slot, size the event to a sensible
// default block, and — when the operator moves the start — slide the end so the
// duration is preserved (rather than leaving a broken/backwards range).
const DEFAULT_SLOT_MIN = 30;
const DEFAULT_DURATION_MS = 60 * 60_000;

/** Round ``d`` UP to the next ``stepMin`` boundary (seconds/ms zeroed). "Now"
 *  at 14:12 → 14:30; 14:45 → 15:00; already-on-boundary is kept. */
function roundUpToSlot(d: Date, stepMin: number): Date {
  const r = new Date(d);
  r.setSeconds(0, 0);
  const rem = r.getMinutes() % stepMin;
  if (rem !== 0) r.setMinutes(r.getMinutes() + (stepMin - rem));
  return r;
}

/** ``add_conference`` rides the wire as a real boolean (BooleanRenderer commits
 * ``e.target.checked``) but a legacy ``"false"`` string may arrive — coerce
 * both. Absent ⇒ ``true`` (the BE default: a Meet/Teams link is auto-added). */
function isConferenceOn(v: ActionFieldValue | undefined): boolean {
  if (v === undefined || v === null) return true;
  return v !== false && v !== "false" && v !== 0;
}

const INPUT =
  "min-w-0 flex-1 bg-transparent text-[12.5px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";
const ROW = "flex items-center gap-2.5 border-hairline border-t px-3 py-2";
const LEAD_ICON = "h-4 w-4 shrink-0 text-ink-4";
// Content-width chip (``w-auto`` overrides the primitive's ``w-full`` so start
// → end sit inline on ONE row, Notion/Amie-style, instead of stacking as two
// full-width bars). Hairline at rest, warm-orange border on hover/focus.
const PICKER_TRIGGER =
  "w-auto shrink-0 whitespace-nowrap rounded-md border border-hairline bg-white/70 px-2.5 py-1 text-[12.5px] text-ink-1 tabular-nums transition-colors hover:border-ando-orange/40 focus-visible:border-ando-orange/50";

/**
 * CalendarCreateEventPreview — the HITL editor for ``calendar_create_event``.
 *
 * Controlled (editable) → ONE compact event card: an inline title (the header),
 * a single "when" row (shared ``DateTimePicker`` primitives, start → end + live
 * duration), then icon-led rows for location / attendees, a provider-aware
 * video-meeting toggle (the BE auto-generates the Meet/Teams link when on), and
 * a notes area — all in one bordered sheet. Uncontrolled (post-execution) → the
 * read-only summary via ``VendorPreviewShell``.
 */
export const CalendarCreateEventPreview: React.FC<CalendarCreateEventPreviewProps> = ({
  proposedFields,
  executedFields,
  schema,
  values,
  errors,
  onChange,
  disabled,
  className,
  loadPeopleOptions,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  const display = executedFields ?? proposedFields;
  const live = values ?? display;
  const commit = useEnvelopeCommit<CalendarFieldName>(values, onChange);

  const title = asString(live.title);
  const start = asString(live.start_time);
  const end = asString(live.end_time);
  const location = asString(live.location);
  const attendees = asString(live.attendees);
  const notes = asString(live.description);
  const recurrence = asString(live.recurrence);
  const duration = formatDuration(start, end);

  // Short human recurrence summary for the read-only (post-execution) card.
  const recurrenceSummary = React.useMemo(() => {
    const rule = parseRRule(recurrence);
    if (!rule) return "";
    switch (rule.freq) {
      case "DAILY":
        return i18n._(msg`Ogni giorno`);
      case "WEEKLY":
        return i18n._(msg`Ogni settimana`);
      case "MONTHLY":
        return i18n._(msg`Ogni mese`);
      case "YEARLY":
        return i18n._(msg`Ogni anno`);
    }
  }, [recurrence, i18n]);

  // Provider is chosen by the calendar account (not the operator) → the label
  // simply reflects which meeting the BE will attach when the toggle is on.
  const provider = asString(live.provider) || asString(display.provider);
  const meetingLabel =
    meetingProviderBrand(detectMeetingProvider({ provider })) ?? i18n._(msg`Videochiamata`);
  const conferenceOn = isConferenceOn(live.add_conference);

  const startReadonly = specOfName(schema, "start_time")?.readonly;
  const endReadonly = specOfName(schema, "end_time")?.readonly;

  // Flips ``true`` the moment the operator touches EITHER time picker, so the
  // smart-default seeding below never fights a manual edit (including a
  // deliberate clear). It is deliberately NOT a "seeded once" latch: the effect
  // must be free to RE-ASSERT the defaults across renders because
  // ``ActionDrawer`` re-seeds its ``values`` from ``proposedFields`` in a mount
  // effect that runs AFTER this child's (React flushes child effects before
  // parent effects) — a one-shot latch would seed, get clobbered back to empty,
  // and never recover. Gating on "user hasn't touched" instead lets the next
  // render re-seed and stick, while a real edit stops it for good.
  const timeTouchedRef = React.useRef(false);

  // Smart defaults (SOTA calendar composer): when the form opens with no
  // AI-proposed start (the common "crea evento" case), pre-fill start = next
  // :00/:30 from now and end = start + 1h so the operator confirms instead of
  // typing two dates from scratch. Only while editable, only for empty+writable
  // fields — an AI-proposed time or a locked field is never overwritten.
  React.useEffect(() => {
    if (!isControlled || disabled || timeTouchedRef.current) return;
    if (start || startReadonly) return;
    const seedStart = roundUpToSlot(new Date(), DEFAULT_SLOT_MIN);
    const patch: Record<string, ActionFieldValue> = { start_time: formatWire(seedStart) };
    if (!end && !endReadonly) {
      patch.end_time = formatWire(new Date(seedStart.getTime() + DEFAULT_DURATION_MS));
    }
    onChange?.({ ...values, ...patch });
  }, [isControlled, disabled, start, end, startReadonly, endReadonly, values, onChange]);

  // ── Read-only fallback (post-execution / uncontrolled) ──────────────────
  if (!isControlled) {
    const when = start ? `${formatDateTime(start)}${end ? ` → ${formatDateTime(end)}` : ""}` : "";
    return (
      <VendorPreviewShell
        className={className}
        icon={<Calendar className="h-4 w-4" />}
        title={title || i18n._(msg`Senza titolo`)}
        caption={i18n._(msg`Nuovo evento in calendario`)}
      >
        <VendorPreviewField
          label={i18n._(msg`Quando`)}
          value={when ? <span className="tabular-nums">{when}</span> : null}
          hideEmpty
          wide
        />
        <VendorPreviewField label={i18n._(msg`Durata`)} value={duration} hideEmpty />
        <VendorPreviewField label={i18n._(msg`Ripetizione`)} value={recurrenceSummary} hideEmpty />
        <VendorPreviewField label={i18n._(msg`Luogo`)} value={location} hideEmpty />
        <VendorPreviewField label={i18n._(msg`Partecipanti`)} value={attendees} hideEmpty wide />
        <VendorPreviewField
          label={i18n._(msg`Videochiamata`)}
          value={conferenceOn ? meetingLabel : i18n._(msg`Nessuna`)}
          hideEmpty
        />
        <VendorPreviewField label={i18n._(msg`Note`)} value={notes} hideEmpty wide />
      </VendorPreviewShell>
    );
  }

  // ── Controlled — ONE compact editable event card ─────────────────────────
  return (
    <div className={cn("flex flex-col text-sm", className)}>
      <div className="flex flex-col rounded-xl border border-hairline bg-white/60 transition-colors focus-within:border-ando-orange/30 focus-within:ring-2 focus-within:ring-ando-orange/45">
        {/* Title — the card's own header */}
        <input
          type="text"
          className="w-full bg-transparent px-3 pt-2.5 pb-2 font-semibold text-[15px] text-ink-1 leading-tight placeholder:font-normal placeholder:text-ink-3 focus:outline-none disabled:opacity-60"
          value={title}
          onChange={(e) => commit("title", e.target.value)}
          disabled={disabled || specOfName(schema, "title")?.readonly}
          placeholder={i18n._(msg`Titolo evento`)}
          aria-label={i18n._(msg`Titolo`)}
        />

        {/* When — ONE compact inline row (SOTA calendar composer, Notion/Amie):
            start → end as content-width chips + trailing live duration, wrapping
            gracefully on a narrow card. ``minDate`` blocks past days (start) and
            pre-start days (end) so the range can't go backwards. */}
        <div className={cn(ROW, "flex-wrap gap-x-2 gap-y-1.5")}>
          <Clock className={LEAD_ICON} aria-hidden />
          <DateTimePicker
            value={parseDate(start)}
            withTime
            minDate={new Date()}
            disabled={disabled || specOfName(schema, "start_time")?.readonly}
            triggerClassName={PICKER_TRIGGER}
            placeholder={i18n._(msg`Inizio`)}
            onChange={(next) => {
              timeTouchedRef.current = true;
              if (!next) {
                commit("start_time", "");
                return;
              }
              const nextStart = formatWire(next);
              // Slide the end to preserve the current duration (or the 1h
              // default when end is unset/backwards) — Google-style drag.
              if (endReadonly) {
                commit("start_time", nextStart);
                return;
              }
              const prevStart = parseDate(start);
              const prevEnd = parseDate(end);
              const durMs =
                prevStart && prevEnd && prevEnd > prevStart
                  ? prevEnd.getTime() - prevStart.getTime()
                  : DEFAULT_DURATION_MS;
              onChange?.({
                ...values,
                start_time: nextStart,
                end_time: formatWire(new Date(next.getTime() + durMs)),
              });
            }}
          />
          <span className="text-[11px] text-ink-4" aria-hidden>
            →
          </span>
          <DateTimePicker
            value={parseDate(end)}
            withTime
            minDate={parseDate(start) ?? new Date()}
            disabled={disabled || specOfName(schema, "end_time")?.readonly}
            triggerClassName={PICKER_TRIGGER}
            placeholder={i18n._(msg`Fine`)}
            onChange={(next) => {
              timeTouchedRef.current = true;
              commit("end_time", next ? formatWire(next) : "");
            }}
          />
          {duration ? (
            <span className="ml-auto text-[11px] text-ink-3 tabular-nums">{duration}</span>
          ) : null}
        </div>

        {/* Recurrence — Google-style presets (computed from the start date) +
            a full custom builder. Emits an RRULE string the BE feeds to Google
            natively / translates to Graph for Outlook. */}
        <div className={ROW}>
          <Repeat className={LEAD_ICON} aria-hidden />
          <RecurrenceField
            value={recurrence}
            onChange={(next) => commit("recurrence", next)}
            start={parseDate(start)}
            disabled={disabled || specOfName(schema, "recurrence")?.readonly}
          />
        </div>

        {/* Location */}
        <label className={ROW}>
          <MapPin className={LEAD_ICON} aria-hidden />
          <input
            type="text"
            className={INPUT}
            value={location}
            onChange={(e) => commit("location", e.target.value)}
            disabled={disabled || specOfName(schema, "location")?.readonly}
            placeholder={i18n._(msg`Aggiungi luogo o link`)}
            aria-label={i18n._(msg`Luogo`)}
          />
        </label>

        {/* Attendees — contact autocomplete: the SAME chip picker the email
            ``to/cc/bcc`` use, scoped to the chosen calendar account's People API
            (Google People + MS Graph). Degrades to free-text chips when no
            ``loadPeopleOptions`` / no account is selected. */}
        <div className={ROW}>
          <Users className={LEAD_ICON} aria-hidden />
          <RecipientsInput
            className="min-w-0 flex-1"
            value={asStringArray(live.attendees)}
            onChange={(next) => commit("attendees", next)}
            loadPeopleOptions={loadPeopleOptions}
            connectionId={asString(live.calendar_connection_id) || null}
            disabled={disabled || specOfName(schema, "attendees")?.readonly}
            placeholder={i18n._(msg`Invitati — cerca contatti…`)}
            ariaLabel={i18n._(msg`Partecipanti`)}
            field={specOfName(schema, "attendees")}
          />
        </div>

        {/* Video meeting — provider-aware toggle (Meet for Google, Teams for
            Outlook). On ⇒ the BE auto-generates & attaches the join link. */}
        <div className={ROW}>
          <Video className={LEAD_ICON} aria-hidden />
          <span className="min-w-0 flex-1 text-[12.5px] text-ink-1">
            {conferenceOn ? meetingLabel : i18n._(msg`Aggiungi videochiamata`)}
            {conferenceOn ? (
              <span className="ml-1.5 text-[11px] text-ink-3">
                {i18n._(msg`link generato automaticamente`)}
              </span>
            ) : null}
          </span>
          <Switch
            checked={conferenceOn}
            onCheckedChange={(next) => commit("add_conference", next)}
            disabled={disabled || specOfName(schema, "add_conference")?.readonly}
            aria-label={i18n._(msg`Videochiamata`)}
          />
        </div>

        {/* Notes */}
        <textarea
          className="min-h-[64px] w-full resize-y border-hairline border-t bg-transparent px-3 py-2 text-[12.5px] text-ink-1 leading-relaxed placeholder:text-ink-3 focus:outline-none disabled:opacity-60"
          value={notes}
          onChange={(e) => commit("description", e.target.value)}
          disabled={disabled || specOfName(schema, "description")?.readonly}
          placeholder={i18n._(msg`Note (opzionale)`)}
          aria-label={i18n._(msg`Note`)}
        />
      </div>
      {errors?.start_time || errors?.end_time ? (
        <p className="px-1 pt-1 text-[11px] text-rose-500">
          {errors.start_time?.message ?? errors.end_time?.message}
        </p>
      ) : null}
    </div>
  );
};
CalendarCreateEventPreview.displayName = "CalendarCreateEventPreview";
